"use client"

import {
  addCampaignSubscriber,
  deleteCampaignSubscriber,
  fetchCampaignSubscriberId,
  fetchCampaignSubscribers,
  fetchCampaigns,
  updateCampaignSubscriber,
} from "@/(admin)/modules/campaign-subscribers/services/campaignSubscriberService"
import { CAMPAIGN_SUBSCRIBERS } from "@/constants/admin-paths"
import { useCheckPermission } from "@/hook/use-check-permission"
import { t } from "@/languages/i18n"
import { PaginationMeta } from "@/types/event"
import {
  CampaignData,
  CampaignSubscriberData,
  CampaignSubscriberFieldType,
  CampaignSubscriberFilterValues,
  CampaignSubscriberPayload,
  StrapiPermissions,
} from "@/types/types"
import { createPath } from "@/utils/create-path"
import { generateDuplicateName } from "@/utils/duplicate-name-helper"
import { App, FormInstance } from "antd"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

interface SearchParams {
  [key: string]: string | number | boolean | undefined
}

interface UseCampaignSubscribersOptions {
  pageSize?: number
  sortField?: string
  sortOrder?: "asc" | "desc"
}

export const useCampaignSubscribers = (options: UseCampaignSubscribersOptions = {}) => {
  const { pageSize: viewPageSize = 20, sortField: viewSortField, sortOrder: viewSortOrder } = options
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { message, modal } = App.useApp()

  const [dataItem, setDataItem] = useState<CampaignSubscriberData[]>([])
  const [permissions, setPermissions] = useState<StrapiPermissions>()
  const [loading, setLoading] = useState(false)
  const [metaData, setMetaData] = useState<PaginationMeta>()
  const [totalItem, setTotalItem] = useState(0)
  const [campaignOptions, setCampaignOptions] = useState<{ value: number; label: string }[]>([])
  const [filterValues, setFilterValues] = useState<CampaignSubscriberFilterValues>({})
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [bulkAction, setBulkAction] = useState<string>("")
  const [needRefresh, setNeedRefresh] = useState<boolean>(false)
  const inFlightRequestKeyRef = useRef<string | null>(null)
  // Drops out-of-order responses so a slower stale request can't overwrite the latest.
  const reqSeq = useRef(0)
  const [pagination, setPagination] = useState<PaginationMeta>({
    page: 1,
    pageSize: viewPageSize,
    total: 0,
    pageCount: 0,
  })

  // pageSize is owned by "Configure view" — reset to page 1 when it (or the sort)
  // changes. No separate "sync page size into pagination" effect (that fed the
  // double fetch — RC1); the fetch reads viewPageSize directly.
  useEffect(() => {
    setPagination(prev => (prev.page === 1 ? prev : { ...prev, page: 1 }))
  }, [viewPageSize, viewSortField, viewSortOrder])

  const updateUrl = useCallback((newParams: Record<string, any>) => {
    const current = new URLSearchParams(Array.from(searchParams.entries()))

    Object.entries(newParams).forEach(([key, value]) => {
      // Loại bỏ pagination khỏi search value trên URL
      if (key === "page" || key === "pageSize") {
        return
      }

      if (value === undefined || value === null || value === "") {
        current.delete(key)
      } else {
        current.set(key, String(value))
      }
    })

    const search = current.toString()
    const query = search ? `?${search}` : ""
    router.push(`${pathname}${query}`)
  }, [pathname, router, searchParams])

  const fetchCampaignsData = useCallback(async () => {
    try {
      const response = await fetchCampaigns()
      if (response && response.length > 0) {
        setCampaignOptions(
          response.map((campaign: CampaignData) => ({
            value: String(campaign.id),
            label: campaign.attributes.name,
          })),
        )
      }
    } catch (error) {
      message.error(t("Lỗi khi lấy chiến dịch. Vui lòng thử lại."))
    }
  }, [message])

  const { hasPermission } = useCheckPermission()

  useEffect(() => {
    fetchCampaignsData()
  }, [fetchCampaignsData])

  useEffect(() => {
    if (typeof window !== "undefined") {
      const storedPermissions = JSON.parse(localStorage.getItem("permissions") || "{}")
      setPermissions(storedPermissions)
    }
  }, [])

  const fetchCampaignSubscribersData = useCallback(
    async (searchTerms?: SearchParams) => {
      const params = {
        page: String(pagination.page),
        pageSize: String(viewPageSize),
        ...searchTerms,
      }
      const requestKey = JSON.stringify(params)

      if (inFlightRequestKeyRef.current === requestKey) {
        return
      }

      inFlightRequestKeyRef.current = requestKey
      const seq = ++reqSeq.current
      setLoading(true)
      try {
        const allResponse = await fetchCampaignSubscribers(params)
        if (seq !== reqSeq.current) return // superseded by a newer request
        const response = allResponse && allResponse.data
        const meta = allResponse && allResponse.meta
        setMetaData(meta)

        if (response && response.length > 0) {
          setDataItem(response)
          setTotalItem(meta?.pagination?.total || 0)
        } else {
          setDataItem([])
          setTotalItem(0)
        }

        // Only sync page/total/pageCount — pageSize stays owned by viewConfig so
        // the response can't feed a fetch loop (RC2).
        if (meta?.pagination) {
          const nextPage = meta.pagination.page || 1
          const nextTotal = meta.pagination.total || 0
          const nextPageCount = meta.pagination.pageCount || 0
          setPagination(prev =>
            prev.page === nextPage && prev.total === nextTotal && prev.pageCount === nextPageCount
              ? prev
              : { ...prev, page: nextPage, total: nextTotal, pageCount: nextPageCount },
          )
        }

        return response
      } catch (error) {
        message.error(t("Lỗi khi lấy đối tác chiến dịch. Vui lòng thử lại."))
      } finally {
        if (inFlightRequestKeyRef.current === requestKey) {
          inFlightRequestKeyRef.current = null
        }
        if (seq === reqSeq.current) setLoading(false)
      }
    },
    [message, pagination.page, viewPageSize],
  )

  useEffect(() => {
    const params: Record<string, any> = {}
    searchParams.forEach((value, key) => {
      params[key] = value
    })

    const { page, pageSize, sort, ...filters } = params

    // Vẫn giữ logic cập nhật pagination vào state nhưng không đẩy lên URL từ updateUrl
    if (page || pageSize) {
      const nextPage = Number(page) || 1
      const nextPageSize = Number(pageSize) || 20

      setPagination(prev => {
        if (prev.page === nextPage && prev.pageSize === nextPageSize) {
          return prev
        }

        return {
          ...prev,
          page: nextPage,
          pageSize: nextPageSize,
        }
      })
    }

    setFilterValues(filters)
    // Apply the "Configure view" default sort when the URL specifies none.
    if (!sort && viewSortField) {
      params.sort = `${viewSortField}:${viewSortOrder ?? "desc"}`
    }
    fetchCampaignSubscribersData(params)
  }, [searchParams, fetchCampaignSubscribersData, viewSortField, viewSortOrder])

  const fetchCampaignSubscriberByIdAndSetForm = useCallback(
    async (id: number, form?: FormInstance) => {
      try {
        const response = await fetchCampaignSubscriberId(id)

        if (!response || !response.attributes) {
          message.error(t("Không tìm thấy đối tác chiến dịch."))
          return { subscriber: null, formData: null }
        }

        const subscriberData = response.attributes

        const formData: CampaignSubscriberFieldType = {
          fullname: subscriberData.fullname || "",
          phone: subscriberData.phone || "",
          email: subscriberData.email || "",
          invoiceNumber: subscriberData.invoiceNumber || "",
          branch: subscriberData.branch || "",
          purchasingDate: subscriberData.purchasingDate || "",
          resolved: subscriberData.resolved !== undefined ? subscriberData.resolved : false,
          campaign: subscriberData.campaign?.data?.id || undefined,
        }

        if (form) {
          form.setFieldsValue(formData)
        }

        return { subscriber: response, formData }
      } catch (error) {
        console.error("Lỗi khi lấy đối tác chiến dịch: ", error)
        message.error(t("Lỗi khi lấy đối tác chiến dịch"))
        return { subscriber: null, formData: null }
      }
    },
    [message],
  )

  const handleDelete = useCallback(
    async (id: number, filterValues?: SearchParams, sortPaginationValues?: SearchParams) => {
      modal.confirm({
        title: t("Xác nhận xóa"),
        content: t("Bạn có muốn xóa đối tác chiến dịch này ?"),
        okText: t("Xóa"),
        cancelText: t("Hủy"),
        onOk: async () => {
          try {
            setLoading(true)
            await deleteCampaignSubscriber(id)
            const currentParams = {
              ...filterValues,
              ...sortPaginationValues,
            }
            fetchCampaignSubscribersData(currentParams)
            message.success(t("Xóa đối tác chiến dịch thành công"))
          } catch {
            message.error(t("Lỗi xóa đối tác chiến dịch"))
          }
        },
      })
    },
    [message, fetchCampaignSubscribersData],
  )

  const handleBulkDelete = useCallback(
    async (filterValues?: SearchParams, sortPaginationValues?: SearchParams) => {
      try {
        setLoading(true)
        const ids = selectedRowKeys.map(id => Number(id))
        await Promise.all(ids.map(id => deleteCampaignSubscriber(id)))
        setSelectedRowKeys([])
        const currentParams = {
          ...filterValues,
          ...sortPaginationValues,
        }
        fetchCampaignSubscribersData(currentParams)
        message.success(t("Xóa các đối tác chiến dịch thành công"))
      } catch {
        message.error(t("Lỗi xóa nhiều đối tác chiến dịch"))
      } finally {
        setLoading(false)
      }
    },
    [selectedRowKeys, message, fetchCampaignSubscribersData],
  )

  const handleApplyBulkAction = useCallback(
    (filterValues?: SearchParams, sortPaginationValues?: SearchParams) => {
      if (bulkAction === "delete" && selectedRowKeys.length > 0) {
        modal.confirm({
          title: t("Xác nhận xóa"),
          content: t("Bạn có chắc chắn muốn xóa những đối tác chiến dịch này?"),
          okText: t("Xóa"),
          cancelText: t("Hủy"),
          onOk: () => handleBulkDelete(filterValues, sortPaginationValues),
        })
      } else if (selectedRowKeys.length === 0) {
        message.warning(t("Vui lòng chọn đối tác chiến dịch để thực hiện hành động"))
      } else {
        message.warning(t("Vui lòng chọn hành động hợp lệ"))
      }
    },
    [bulkAction, selectedRowKeys, message, handleBulkDelete],
  )

  const handleEdit = useCallback(
    (id: number) => {
      const currentParams = new URLSearchParams(window.location.search)
      const targetPath = createPath(CAMPAIGN_SUBSCRIBERS.EDIT.PATH, currentParams, { id })
      router.push(targetPath)
    },
    [router],
  )

  const handleRefresh = useCallback(
    (filterRef?: { current: { filterFunction: () => void } }) => {
      if (filterRef?.current) {
        filterRef.current.filterFunction()
      }
      setNeedRefresh(!needRefresh)
      router.replace(pathname)
    },
    [needRefresh, pathname],
  )

  const onSearch = useCallback(
    (filters: object) => {
      const typedFilters = filters as Record<string, any>

      if (Object.keys(typedFilters).length === 0) {
        router.replace(pathname);
        return;
      }
      setPagination(prev => ({ ...prev, page: 1 }))
      updateUrl(typedFilters)
    },
    [updateUrl, pathname, router],
  )

  const onTableChange = useCallback((paginationConfig: any, filters: any, sorter: any) => {
    setPagination(prev => ({
      ...prev,
      page: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    }))

    const newParams: Record<string, any> = {}
    if (sorter && !Array.isArray(sorter) && sorter.field && sorter.order) {
      const field = Array.isArray(sorter.field) ? sorter.field[1] : sorter.field
      const order = sorter.order === "ascend" ? "asc" : "desc"
      newParams.sort = `${field}:${order}`
    }

    updateUrl(newParams)
  }, [updateUrl])

  const handleUpsertCampaignSubscriber = useCallback(
    async (values: CampaignSubscriberFieldType, subscriberId?: number) => {
      const dataPayload: CampaignSubscriberPayload = {
        data: {
          fullname: values.fullname,
          phone: values.phone,
          email: values.email,
          invoiceNumber: values.invoiceNumber || "",
          branch: values.branch,
          purchasingDate: values.purchasingDate,
          resolved: values.resolved !== undefined ? values.resolved : false,
          campaign: values.campaign || undefined,
        },
      }

      try {
        if (subscriberId) {
          await updateCampaignSubscriber(subscriberId, dataPayload)
          message.success(t("Cập nhật đối tác chiến dịch thành công!"))
          return { success: true, id: subscriberId, isEdit: true }
        } else {
          const response = await addCampaignSubscriber(dataPayload)
          message.success(t("Tạo mới đối tác chiến dịch thành công!"))
          return { success: true, id: response.data.id, isEdit: false }
        }
      } catch (error) {
        if (subscriberId) {
          message.error(t("Lỗi khi cập nhật đối tác chiến dịch. Vui lòng thử lại."))
        } else {
          message.error(t("Lỗi khi tạo mới đối tác chiến dịch. Vui lòng thử lại."))
        }
        return { success: false, id: null, isEdit: !!subscriberId }
      }
    },
    [message],
  )

  const handleDuplicate = useCallback(
    async (subscriber: CampaignSubscriberData, filterValues?: SearchParams, sortPaginationValues?: SearchParams) => {
      try {
        setLoading(true)

        const existingFullnames = dataItem
          .map(item => item.attributes.fullname)
          .filter((name): name is string => !!name)

        const duplicatedData: CampaignSubscriberFieldType = {
          fullname: generateDuplicateName(subscriber.attributes.fullname || "", existingFullnames),
          phone: subscriber.attributes.phone,
          email: subscriber.attributes.email,
          invoiceNumber: subscriber.attributes.invoiceNumber || undefined,
          branch: subscriber.attributes.branch,
          purchasingDate: subscriber.attributes.purchasingDate,
          resolved: subscriber.attributes.resolved !== null ? subscriber.attributes.resolved : undefined,
          campaign: subscriber.attributes.campaign?.data?.id || undefined,
        }

        await handleUpsertCampaignSubscriber(duplicatedData)

        const currentParams = {
          ...filterValues,
          ...sortPaginationValues,
        }
        await fetchCampaignSubscribersData(currentParams)
        message.success(t("Nhân bản đối tác chiến dịch thành công!"))
      } catch (error) {
        message.error(t("Lỗi khi nhân bản đối tác chiến dịch. Vui lòng thử lại."))
      } finally {
        setLoading(false)
      }
    },
    [message, fetchCampaignSubscribersData, handleUpsertCampaignSubscriber, dataItem],
  )

  const initialFilterValues = useMemo(() => {
    const params: Record<string, any> = {}
    searchParams.forEach((value, key) => {
      if (!["page", "pageSize", "sort"].includes(key)) {
        params[key] = value
      }
    })
    return params
  }, [searchParams])

  return {
    dataItem,
    loading,
    metaData,
    totalItem,
    campaignOptions,
    selectedRowKeys,
    filterValues,
    bulkAction,
    needRefresh,
    pagination,
    initialFilterValues,

    actions: {
      fetchCampaignSubscribersData,
      fetchCampaignSubscriberByIdAndSetForm,
      fetchCampaignsData,
      handleDelete,
      handleBulkDelete,
      hasPermission,
      handleApplyBulkAction,
      handleEdit,
      handleDuplicate,
      handleRefresh,
      setFilterValues,
      onSearch,
      onTableChange,
      handleUpsertCampaignSubscriber,
      setSelectedRowKeys,
      setBulkAction,
      setPagination,
    },

    setters: {
      setDataItem,
      setLoading,
      setMetaData,
      setTotalItem,
      setCampaignOptions,
      setSelectedRowKeys,
      setBulkAction,
      setNeedRefresh,
      setPagination,
    },
  }
}
